import React from 'react';
import {
  Drawer,
  Button,
  Row,
  Col,
  message,
  Table,
} from 'antd';
import { CloseOutlined, DownloadOutlined, BankOutlined } from '@ant-design/icons';
import { ReceivablesRequestActionStyles } from './Receivables.actionDrawerStyles';
import { StylesProps } from 'src/library/types';
import withStyles from 'react-jss';
import { raiseSettlementDispute, settleInvoice } from './Receivables.api';
import { transactionColumns, actionTypeAndModalTitleMapping } from './Receivables.utils';
import { formatDateTime, formatNumber, transformName } from 'src/utils/utils';
import CommentsAndEventSection from './Receivables.commentAndEventSection';
import { get } from 'lodash';

interface IProps extends StylesProps<ReturnType<typeof ReceivablesRequestActionStyles>> {
  isVisible: boolean;
  actionType: string;
  data: any;
  handleClose: (onSuccess?: boolean) => void;
  handleReload: () => void;
  isReadonly?: boolean;
  columnsList: any[];
}

const SettleOrRaiseDisputeInvoiceActionDrawer = (props: IProps): React.ReactElement => {
  const {
    classes,
    data,
    isVisible,
    handleClose,
    handleReload,
    actionType,
    isReadonly = false,
    columnsList = [],
  } = props;

  const generateColumnForTransactionsLog = () => {
    const columnData: any = [];
    const indexList = transactionColumns;
    indexList.forEach((item) => {
      const itemToPush = {};
      itemToPush['title'] = item.pretty_name;
      itemToPush['dataIndex'] = item.column_id;
      itemToPush['key'] = item.column_id;
      itemToPush['width'] = item.width || '150px';
      columnData.push(itemToPush);
    });
    return columnData;
  };

  const transactionDetails = data?.transaction_details || [];
  const raisedInvoiceUrl = data?.invoice_pdf_url || '';
  const isValue = (val) => (
    (Array.isArray(val) || typeof val === 'object') ? '-' : (val ?? '-')
  );

  const transactionDetailsWithKey = transactionDetails.map((item, idx) => ({
    ...item,
    key: item.transaction_ref_no ? `${item.transaction_ref_no}_${idx}` : `row_${idx}`,
  }));

  const renderBillingCycle = () => {
    if (data?.start_date && data?.end_date) {
      const startDate = formatDateTime(data.start_date, 'DD MMM YY');
      const endDate = formatDateTime(data.end_date, 'DD MMM YY');
      return `${startDate} - ${endDate}`;
    }
    return '';
  };

  const renderDate = (date) => {
    if (Array.isArray(date) || typeof date === 'object') {
      return '-';
    }
    return formatDateTime(date, 'DD MMM YY', '-');
  };

  const getColumnsToShow = (data: any) => {
    const filteredColumns = (columnsList || []).filter((col) => col.column_id !== 'actions'
      && col.column_id !== 'created_at');

    const mappedColumns = filteredColumns.map((col) => {
      let content: React.ReactNode = isValue(data[col.column_id]);
      switch (col.column_id) {
        case 'billing_cycle':
          content = renderBillingCycle();
          break;
        case 'status':
          content = isValue(data.status?.toUpperCase().replace(/_/g, ' '));
          break;
        case 'created_at':
          content = renderDate(data.created_at);
          break;
        case 'net_amount':
        case 'total_tax':
        case 'final_amount':
          content = formatNumber(data[col.column_id]);
          break;
        case 'invoice_number':
          content = get(data, col.column_id, '-') || '-';
          break;
        default:
          content = transformName(get(data, col.column_id, '-') || '-');
          break;
      }
      const style = col.column_id === 'invoice_number'
        ? { minWidth: '250px', width: '250px' }
        : {};
      return {
        hint: col.pretty_name,
        content,
        span: 4,
        style,
      };
    });

    mappedColumns.push({
      hint: 'Supporting Calculations',
      content: (
        <div style={{
          width: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 40,
        }}
        >
          {data?.invoice_computation_excel_url ? (
            <a
              href={data.invoice_computation_excel_url}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                size="small"
              >
                Download
              </Button>
            </a>
          ) : (
            <span style={{ color: 'grey' }}>-</span>
          )}
        </div>
      ),
      span: 8,
      style: {},
    });
    return mappedColumns;
  };

  const renderInfoContainer = (hint, content, span, style = {}, key) => (
    <Col key={key} span={span} className={classes.infoBox} style={style}>
      <div className={classes.hint}>{hint}</div>
      <div className={classes.content} style={hint === 'Invoice Number' ? { wordBreak: 'break-all' } : {}}>
        {content}
      </div>
    </Col>
  );

  const renderDetails = () => (
    <Row className={classes.divFlex}>
      {getColumnsToShow(data).map((i) =>
        renderInfoContainer(i.hint, i.content, i.span, i.style, i.hint))}
    </Row>
  );

  const handleInvoiceAction = async () => {
    try {
      const body = {
        id: String(data?.id),
      };

      let response;
      if (actionType === 'settle_invoice') {
        response = await settleInvoice(body);
      } else if (actionType === 'settle_invoice_dispute') {
        response = await raiseSettlementDispute(body);
      }

      if (!response) {
        throw new Error('No response from server');
      }

      if (response.isSuccess && response?.data?.success) {
        message.success(response.message);
        handleClose(true);
        handleReload();
      } else {
        message.error(response.errorMessage || 'Failed to process request');
      }
    } catch (err) {
      message.error(err.message || 'Failed to process request');
    }
  };

  const renderTitle = () => (
    <div className={classes.drawerTitle}>
      <div className={classes.titleHeading}>
        <CloseOutlined
          onClick={() => handleClose()}
          className={classes.titleClose}
        />
        {isReadonly
          ? 'Settled'
          : actionTypeAndModalTitleMapping[actionType]}
      </div>
      {!isReadonly && (
        <Button
          style={actionType === 'settle_invoice' ? { marginLeft: '650px' } : { marginLeft: '520px' }}
          type="primary"
          onClick={() => handleInvoiceAction()}
        >
          {actionType === 'settle_invoice' ? 'Settle' : 'Raise Settlement Dispute'}
        </Button>
      )}
      <Button
        style={{ marginLeft: '5px' }}
        type="primary"
        onClick={() => handleClose()}
      >
        Cancel
      </Button>
    </div>
  );

  return (
    <Drawer
      title={renderTitle()}
      closable={false}
      onClose={() => handleClose()}
      visible={isVisible}
      width={1000}
    >
      <div className={classes.detailsContainer}>
        {renderDetails()}
      </div>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ flex: 1 }}>
          <CommentsAndEventSection
            classes={classes}
            data={data}
            isReadonly={isReadonly}
          />
        </div>
        <div style={{ borderLeft: '2px solid #f0f0f0', minHeight: '80vh' }} />
        <div style={{ flex: 1 }}>
          <div>
            <iframe
              title="invoice"
              src={raisedInvoiceUrl}
              width="100%"
              height="100vh"
              style={{ marginLeft: '10px', boxSizing: 'content-box', height: '80vh' }}
            />
          </div>
        </div>
      </div>
      <div style={{ marginTop: 24 }}>
        <div style={{ borderTop: '2px solid #f0f0f0', margin: '8px 0' }} />
        <div style={{ marginLeft: '6px', fontSize: '14px', marginTop: '2px', fontWeight: 500 }}>
          <BankOutlined style={{ marginRight: '4px' }} />
          Transaction Details
        </div>
        <div style={{ borderTop: '2px solid #f0f0f0', margin: '8px 0' }} />
        <Table
          columns={generateColumnForTransactionsLog()}
          dataSource={transactionDetailsWithKey}
          pagination={false}
          size="small"
          bordered
          rowKey="key"
          locale={{ emptyText: 'No transaction details available' }}
        />
      </div>
    </Drawer>
  );
};

export default withStyles(ReceivablesRequestActionStyles)(SettleOrRaiseDisputeInvoiceActionDrawer);
